import React, { useState, useRef } from "react";
import styles from "./ScannerUI.module.css";
import LoadingIndicator from "./LoadingIndicator";

function CameraSelect({ cameras, onChangeCamera, triggerRipple }) {
  const [open, setOpen] = useState(false);

  const toggleDropdown = () => setOpen((prev) => !prev);

  const handleSelect = (camera) => {
    onChangeCamera(camera);
    setOpen(false);
  };

  return (
    <div className={styles.cameraSelectWrapper}>
      <div
        className={styles.cameraSelectDisplay}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          triggerRipple(e);
          toggleDropdown();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") toggleDropdown();
        }}
      >
        Camera Select {open ? "▲" : "▼"}
      </div>

      {open && (
        <ul className={styles.cameraSelectList} role="listbox" tabIndex={-1}>
          {cameras.map((camera) => (
            <li
              key={camera.deviceId}
              className={styles.cameraSelectItem}
              role="option"
              tabIndex={0}
              aria-selected={false}
              onClick={() => handleSelect(camera)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleSelect(camera);
                }
              }}
            >
              {camera.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ScannerUI({
  tipText = "",
  cameraList = [],
  loadingCamera = false,
  cameraError = false,
  capabilities = { zoom: false, torch: false },
  cameraConstraints = { zoom: { min: 1, max: 8, step: 1 } },
  onClose,
  onUpdateCamera,
  onToggleTorch,
  onZoomUpdate,
  videoRef,
}) {
  const [zoom, setZoom] = useState(1);
  const tipTextRef = useRef(null);

  const handleChangeCamera = (camera) => {
    onUpdateCamera?.(camera.deviceId);
  };

  const handleZoomChange = (e) => {
    const value = Number(e.target.value);
    setZoom(value);
    onZoomUpdate?.(value);
  };

  const triggerRipple = (e) => {
    const target = e.currentTarget;
    target.classList.remove(styles.rippleActive);

    // Force reflow to restart animation
    // eslint-disable-next-line no-unused-expressions
    target.offsetWidth;

    target.classList.add(styles.rippleActive);

    // Position ripple at click point
    const rect = target.getBoundingClientRect();
    const ripple = target.style;
    ripple.setProperty("--ripple-x", `${e.clientX - rect.left}px`);
    ripple.setProperty("--ripple-y", `${e.clientY - rect.top}px`);
  };

  return (
    <div className={styles.fixedFull}>
      {/* Video Layer */}
      <div className={styles.videoContainer}>
        <video ref={videoRef} muted autoPlay playsInline />
      </div>

      {/* UI Layer */}
      <div className={styles.uiLayer}>
        {loadingCamera && (
          <div className={styles.absoluteCenter}>
            <LoadingIndicator
              title="Loading Camera"
              description="Please wait while we initialize your camera"
            />
          </div>
        )}

        {cameraError && (
          <div className={styles.absoluteCenter}>
            <div className={styles.tipText}>
              There was an error loading your camera. Please refresh the page.
            </div>
          </div>
        )}

        <button type="button" className={styles.closeButton} onClick={onClose}>
          ✕
        </button>

        {!cameraError && !loadingCamera && (
          <div className={styles.absoluteBottom}>
            {/* Zoom Slider */}
            <div className={styles.zoomSlider}>
              <span className={styles.zoomIcon}>−</span>
              <input
                type="range"
                value={zoom}
                min={cameraConstraints.zoom.min}
                max={cameraConstraints.zoom.max}
                step={cameraConstraints.zoom.step}
                onChange={handleZoomChange}
                className={styles.zoomSliderInput}
              />
              <span className={styles.zoomIcon}>+</span>
            </div>

            {/* Tip Text */}
            {tipText && (
              <div ref={tipTextRef} className={styles.tipText}>
                {tipText}
              </div>
            )}

            {/* Camera Select + Torch */}
            <div
              className={
                capabilities.torch
                  ? styles.cameraSelectBar
                  : styles.cameraSelectBarSingle
              }
            >
              <CameraSelect
                cameras={cameraList}
                onChangeCamera={handleChangeCamera}
                triggerRipple={triggerRipple}
              />

              {capabilities.torch && (
                <button
                  type="button"
                  className={styles.torchButton}
                  onClick={(e) => {
                    triggerRipple(e);
                    onToggleTorch();
                  }}
                  tabIndex={0}
                  aria-pressed="false"
                >
                  Light On/Off
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
